import { useEffect, useState, useCallback } from 'react';
import { apiFetch } from '../api';

type Status = { enabled: boolean; dispatch: boolean; message: string | null };

/**
 * Wartungsmodus-Toggle im Header.
 * Sichtbar nur für die Organisation 'SUPER'.
 * Pollt /system/maintenance alle 30 s.
 */
export default function MaintenanceToggle() {
  const org = (() => {
    try { return localStorage.getItem('organization') || ''; } catch { return ''; }
  })();
  const isSuper = org === 'SUPER';

  const [status, setStatus] = useState<Status | null>(null);
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [draftMessage, setDraftMessage] = useState<string>('');
  const [draftDispatch, setDraftDispatch] = useState<boolean>(false);

  const load = useCallback(async () => {
    try {
      const data = await apiFetch('/system/maintenance');
      setStatus({
        enabled: !!data?.enabled,
        dispatch: !!data?.dispatch,
        message: data?.message ?? null,
      });
    } catch {
      // ignore – Endpoint evtl. (noch) nicht erreichbar
    }
  }, []);

  useEffect(() => {
    if (!isSuper) return;
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [isSuper, load]);

  if (!isSuper || !status) return null;

  const off = status.enabled; // enabled = OFFLINE (Portal)
  const next = !status.enabled;

  function openConfirm() {
    setError(null);
    setDraftMessage(status?.message ?? '');
    // Beim Aktivieren defaultmäßig auch Dispatch sperren;
    // beim Deaktivieren defaultmäßig Dispatch ebenfalls freischalten.
    setDraftDispatch(next ? (status?.dispatch || true) : false);
    setConfirmOpen(true);
  }

  async function applyToggle() {
    if (!status) return;
    setError(null);
    setLoading(true);
    try {
      const trimmed = draftMessage.trim();
      const msgToSend = next ? (trimmed.length ? trimmed : null) : null;
      const res = await apiFetch('/system/maintenance', {
        method: 'PUT',
        body: JSON.stringify({
          enabled: next,
          dispatch: next ? !!draftDispatch : false,
          message: msgToSend,
        }),
      });
      setStatus({
        enabled: !!res?.enabled,
        dispatch: !!res?.dispatch,
        message: res?.message ?? null,
      });
      setConfirmOpen(false);
    } catch (e: any) {
      setError(e?.message || 'Fehler beim Umschalten des Wartungsmodus');
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={openConfirm}
        disabled={loading}
        title={
          off
            ? `Wartungsmodus AKTIV (Portal${status.dispatch ? ' + Dispatch' : ''}) – Klick zum Deaktivieren`
            : 'System ONLINE – Klick zum Aktivieren des Wartungsmodus'
        }
        className={
          'flex items-center gap-1.5 px-2 sm:px-3 py-1 rounded-lg shadow text-xs font-semibold transition ' +
          (off
            ? 'bg-red-600 text-white animate-pulse hover:bg-red-700'
            : 'bg-green-600 text-white hover:bg-green-700') +
          (loading ? ' opacity-60 cursor-wait' : '')
        }
      >
        <span className="inline-block w-2 h-2 rounded-full bg-white dark:bg-gray-800" />
        <span className="hidden sm:inline">
          {off ? (status.dispatch ? 'OFFLINE +DISP' : 'OFFLINE') : 'ONLINE'}
        </span>
      </button>

      {confirmOpen && (
        <div
          className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          onClick={() => !loading && setConfirmOpen(false)}
        >
          <div
            className="w-full max-w-md bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className={
              'px-5 py-3 flex items-center gap-2 ' +
              (next ? 'bg-red-600 text-white' : 'bg-green-600 text-white')
            }>
              <span className={
                'inline-block w-2.5 h-2.5 rounded-full bg-white dark:bg-gray-800 ' +
                (next ? 'animate-pulse' : '')
              } />
              <h3 className="font-semibold">
                Wartungsmodus {next ? 'AKTIVIEREN' : 'DEAKTIVIEREN'}?
              </h3>
            </div>
            <div className="px-5 py-4 text-sm text-gray-800 dark:text-gray-100 space-y-2">
              {next ? (
                <>
                  <p>Alle Organisationen <strong>außer SUPER und TESTORG</strong> werden ausgesperrt.</p>
                  <p className="text-gray-600 dark:text-gray-400">
                    Bereits eingeloggte Nutzer sehen eine Wartungsmeldung und werden zum Abmelden aufgefordert.
                  </p>

                  <label className="flex items-start gap-2 pt-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={draftDispatch}
                      onChange={(e) => setDraftDispatch(e.target.checked)}
                      className="mt-0.5 h-4 w-4 accent-red-600"
                    />
                    <span>
                      <span className="font-medium">Dispatch-Modul ebenfalls in Wartung versetzen</span>
                      <span className="block text-xs text-gray-600 dark:text-gray-400">
                        Sperrt die Anmeldung an OrgaPilot.DISPATCH für alle Organisationen außer SUPER und TESTORG.
                      </span>
                    </span>
                  </label>

                  <div className="pt-2">
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Hinweistext für den Login-Dialog (optional)
                    </label>
                    <textarea
                      value={draftMessage}
                      onChange={(e) => setDraftMessage(e.target.value)}
                      rows={3}
                      maxLength={500}
                      placeholder="z. B. Wartungsarbeiten bis ca. 22:00 Uhr. Bitte später erneut versuchen."
                      className="w-full px-2 py-1.5 text-sm rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-red-500"
                    />
                    <div className="mt-1 text-[10px] text-gray-500 dark:text-gray-400 text-right">
                      {draftMessage.length}/500
                    </div>
                  </div>
                </>
              ) : (
                <p>Anmeldungen werden wieder für alle Organisationen freigegeben (Portal und Dispatch).</p>
              )}
              {error && (
                <div className="mt-2 px-3 py-2 rounded bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-xs">
                  {error}
                </div>
              )}
            </div>
            <div className="px-5 py-3 bg-gray-50 dark:bg-gray-900/40 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmOpen(false)}
                disabled={loading}
                className="px-4 py-1.5 text-sm rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700hover:bg-gray-600"
              >
                Abbrechen
              </button>
              <button
                type="button"
                onClick={applyToggle}
                disabled={loading}
                className={
                  'px-4 py-1.5 text-sm font-semibold rounded text-white shadow ' +
                  (next ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700') +
                  (loading ? ' opacity-60 cursor-wait' : '')
                }
              >
                {loading ? 'Wird gespeichert …' : (next ? 'Aktivieren' : 'Deaktivieren')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
